import { InsufficientTokensError } from './api.js';
// Turn a thrown error into something a founder should read.
//
// Every `catch` in this app funnels `err.message` straight into a toast, and
// when the API is momentarily unreachable — a deploy restart, a dropped wifi
// connection, a sleeping laptop — that message is the browser's, not ours. The
// founder gets "Failed to fetch": not Mag's voice, not an explanation, and no
// hint that trying again is the right move.
//
// Each browser words it differently, which is exactly why this cannot be a
// single string comparison at the call site:
//   Chrome/Edge  TypeError: Failed to fetch
//   Safari       TypeError: Load failed
//   Firefox      TypeError: NetworkError when attempting to fetch resource.
//
// A real API error still passes through untouched. The point is to stop
// dressing up a transport failure as a product failure, not to hide detail:
// when the server actually said something, the founder should see it.

/** Is this the org running out of mag tokens?
 *
 *  Two shapes, because the two places it can surface are different. A
 *  request the founder made throws the typed InsufficientTokensError; an
 *  ASYNC orchestrator step (the site scrape) has no request to attach to
 *  and reaches the client as a failed step whose detail is prose. The
 *  prose check is the fallback, not the primary — a message reworded in
 *  the api would silently stop matching, so callers that HAVE the typed
 *  error should keep throwing it. */
export function isOutOfTokens(err: unknown): boolean {
  if (err instanceof InsufficientTokensError) return true;
  const text = err instanceof Error ? err.message : typeof err === 'string' ? err : '';
  return /out of mag tokens/i.test(text);
}

const NETWORK_SIGNATURES = [
  'failed to fetch',
  'load failed',
  'networkerror when attempting to fetch',
  'network request failed',
  'the internet connection appears to be offline',
];

/** Mag-voice copy for a caught error, for use as a toast body.
 *
 *  `fallback` is what a NON-Error throw renders as. Call sites already carried
 *  their own — '', 'try again.', "couldn't save." — and those are context the
 *  helper cannot reinvent, so they come along rather than being flattened. */
export function founderErrorCopy(err: unknown, fallback = ''): string {
  if (!(err instanceof Error)) return fallback;

  // A request the browser gave up on. Distinct from a network failure: the
  // connection worked, the wait did not.
  if (err.name === 'AbortError' || err.name === 'TimeoutError') {
    return 'that took longer than i was willing to wait. try again.';
  }

  const message = err.message.toLowerCase();
  if (NETWORK_SIGNATURES.some((s) => message.includes(s))) {
    return "couldn't reach me just then — usually a blip on my side. try again in a moment.";
  }

  return err.message;
}
